/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import utils from '../../../../common/utils';
import * as constants from '../../constants/ServiceConstants';
import { PROMPT_MANAGEMENT_LANG_KEYS } from '../../constants/StudioTextConstants';
import { mapResolvedDocument, mergeResolvedDocument } from '../../utils/promptApiMappers';
import { normalizeCatalogueRows, normalizeCataloguePagination } from '../../../../common/prompt/catalogueUtils';

const CatalogueRowModel = types.model('StudioCatalogueRowModel', {
  promptRef: types.string,
  promptVersionRef: types.maybeNull(types.string),
  name: types.optional(types.string, ''),
  description: types.optional(types.string, ''),
  comments: types.optional(types.string, ''),
  modifiedBy: types.optional(types.string, ''),
  updatedDate: types.maybeNull(types.string),
  createdDate: types.maybeNull(types.string),
  versionCount: types.maybeNull(types.number),
  promptType: types.optional(types.string, 'text')
});

const normalizeVersionRows = (responseData) => {
  if (!responseData) return [];
  const rows = Array.isArray(responseData) ? responseData : responseData.data ?? responseData.versions ?? [];
  return Array.isArray(rows)
    ? rows.map((v) => mapResolvedDocument({ ...v, id: v.id || v.promptRef })).filter(Boolean)
    : [];
};

const PromptMessageModel = types.model('PromptMessageModel', {
  role: types.maybeNull(types.string),
  content: types.maybeNull(types.string)
});

const ResolvedPromptModel = types.model('ResolvedPromptModel', {
  id: types.maybeNull(types.string),
  version: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  comments: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  promptStart: types.optional(types.array(PromptMessageModel), []),
  promptEnd: types.optional(types.array(PromptMessageModel), []),
  createdDate: types.maybeNull(types.number),
  updatedDate: types.maybeNull(types.string),
  modifiedBy: types.maybeNull(types.string)
});

const StudioPromptModel = types
  .model('StudioPromptModel', {
    activeResolved: types.maybeNull(ResolvedPromptModel),
    versions: types.optional(types.array(ResolvedPromptModel), []),
    catalogue: types.optional(types.array(CatalogueRowModel), []),
    cataloguePagination: types.maybeNull(types.frozen()),
    versionsPromptRef: types.maybeNull(types.string),
    lastError: types.maybeNull(types.string),
    lastMutation: types.maybeNull(types.frozen())
  })
  .actions((self) => {
    const publish = (event, payload) => Framework.getEventManager().publish(event, payload);

    const notifyError = ({ message = '', langMessage = '' }) => {
      Framework.getStoreRegistry().getStore('notificationState').set({
        open: true,
        type: 'error',
        message,
        langMessage
      });
    };

    const fail = (lastError, errorEvent, notification = {}) => {
      applySnapshot(self, { ...getSnapshot(self), lastError });
      notifyError(notification);
      publish(errorEvent);
    };

    return {
      clearLastError() {
        applySnapshot(self, { ...getSnapshot(self), lastError: null });
      },

      setVersions(rows) {
        applySnapshot(self, { ...getSnapshot(self), lastError: null, versions: rows || [] });
      },

      setCatalogue(rows) {
        applySnapshot(self, { ...getSnapshot(self), lastError: null, catalogue: rows || [] });
      },

      setCataloguePagination(pagination) {
        applySnapshot(self, { ...getSnapshot(self), cataloguePagination: pagination || null });
      },

      setActiveResolved(data) {
        if (!data) {
          applySnapshot(self, { ...getSnapshot(self), activeResolved: null });
          return;
        }
        applySnapshot(self, { ...getSnapshot(self), lastError: null, activeResolved: data });
      },

      resetPanelState() {
        applySnapshot(self, {
          activeResolved: null,
          versions: [],
          versionsPromptRef: null,
          catalogue: [],
          cataloguePagination: null,
          lastError: null,
          lastMutation: null
        });
      },

      fetchCatalogue(payload) {
        self.clearLastError();
        publish(constants.GET_PROMPT_CATALOGUE, { payload });
      },

      fetchVersions(payload) {
        self.clearLastError();
        publish(constants.GET_PROMPT_VERSIONS, { payload });
      },

      fetchVersionsIfNeeded(payload, { force = false } = {}) {
        const promptRef = payload?.promptRef;
        if (!promptRef) {
          return;
        }
        if (!force && self.versionsPromptRef === promptRef && self.versions.length > 0) {
          return;
        }
        self.fetchVersions(payload);
      },

      fetchVersion(payload) {
        self.clearLastError();
        publish(constants.GET_PROMPT_VERSION, { payload });
      },

      updatePrompt(payload) {
        self.clearLastError();
        publish(constants.UPDATE_PROMPT, { payload });
      },

      createPrompt(payload) {
        self.clearLastError();
        publish(constants.CREATE_PROMPT, { payload });
      },

      checkNameAvailability(payload) {
        self.clearLastError();
        publish(constants.GET_PROMPT_NAME_AVAILABILITY, { payload });
      },

      handleServiceError(error, fallbackLangKey, errorEvent) {
        const { response } = error;
        let lastError = fallbackLangKey;
        let notification = { langMessage: fallbackLangKey };

        if (response) {
          const { status, data } = response;
          if (status === 401) {
            utils.redirectToLogin();
            publish(errorEvent);
            return;
          }
          const serverMessage = data?.error || data?.message;
          if (serverMessage) {
            lastError = serverMessage;
            notification = { message: serverMessage };
          }
        } else if (error.message === `timeout of ${constants.BERLIN_API_TIMEOUT}ms exceeded`) {
          lastError = PROMPT_MANAGEMENT_LANG_KEYS.REQUEST_TIMEOUT;
          notification = { langMessage: PROMPT_MANAGEMENT_LANG_KEYS.REQUEST_TIMEOUT };
        }

        applySnapshot(self, { ...getSnapshot(self), lastError });
        notifyError(notification);
        publish(errorEvent);
      },

      handleCatalogueResponse(data, { search = '', dataContext = {} } = {}) {
        if (data && data.success !== false) {
          const mapped = normalizeCatalogueRows(data, dataContext);
          const pagination = normalizeCataloguePagination(data);
          self.setCatalogue(mapped);
          self.setCataloguePagination(pagination);
          publish(constants.GET_PROMPT_CATALOGUE_SUCCESS, { data: mapped, pagination, search });
          return;
        }
        const serverMessage = data?.error || data?.message;
        if (serverMessage) {
          fail(serverMessage, constants.GET_PROMPT_CATALOGUE_ERROR, { message: serverMessage });
          return;
        }
        fail(PROMPT_MANAGEMENT_LANG_KEYS.ERROR_CATALOGUE, constants.GET_PROMPT_CATALOGUE_ERROR, {
          langMessage: PROMPT_MANAGEMENT_LANG_KEYS.ERROR_CATALOGUE
        });
      },

      handleVersionsResponse(data, { promptRef = null } = {}) {
        if (data && data.success !== false) {
          const mapped = normalizeVersionRows(data);
          applySnapshot(self, {
            ...getSnapshot(self),
            lastError: null,
            versions: mapped,
            versionsPromptRef: promptRef
          });
          publish(constants.GET_PROMPT_VERSIONS_SUCCESS, { data: mapped, promptRef });
          return;
        }
        const serverMessage = data?.error || data?.message;
        if (serverMessage) {
          fail(serverMessage, constants.GET_PROMPT_VERSIONS_ERROR, { message: serverMessage });
          return;
        }
        fail(PROMPT_MANAGEMENT_LANG_KEYS.ERROR_VERSIONS, constants.GET_PROMPT_VERSIONS_ERROR, {
          langMessage: PROMPT_MANAGEMENT_LANG_KEYS.ERROR_VERSIONS
        });
      },

      handleVersionResponse(data, { promptRef = null, promptVersionRef = null } = {}) {
        if (data && data.success !== false) {
          const mapped = mapResolvedDocument({ ...data, id: data.id || data.promptRef || promptRef });
          if (mapped) {
            self.setActiveResolved(mapped);
          }
          publish(constants.GET_PROMPT_VERSION_SUCCESS, {
            data: mapped || data,
            promptRef,
            promptVersionRef
          });
          return;
        }
        const serverMessage = data?.error || data?.message;
        if (serverMessage) {
          fail(serverMessage, constants.GET_PROMPT_VERSION_ERROR, { message: serverMessage });
          return;
        }
        fail(PROMPT_MANAGEMENT_LANG_KEYS.ERROR_VERSION, constants.GET_PROMPT_VERSION_ERROR, {
          langMessage: PROMPT_MANAGEMENT_LANG_KEYS.ERROR_VERSION
        });
      },

      handleUpdateResponse(data, { promptRef = null } = {}) {
        if (data && data.success !== false) {
          const mutation = {
            promptRef: data.promptRef || data.id || promptRef,
            promptVersionRef: data.promptVersionRef || data.version,
            version: data.version,
            previousVersion: data.previousVersion
          };
          applySnapshot(self, { ...getSnapshot(self), lastError: null, lastMutation: mutation });
          publish(constants.UPDATE_PROMPT_SUCCESS, { data: mutation });
          return;
        }
        const serverMessage = data?.error || data?.message;
        if (serverMessage) {
          fail(serverMessage, constants.UPDATE_PROMPT_ERROR, { message: serverMessage });
          return;
        }
        fail(PROMPT_MANAGEMENT_LANG_KEYS.ERROR_SAVE, constants.UPDATE_PROMPT_ERROR, {
          langMessage: PROMPT_MANAGEMENT_LANG_KEYS.ERROR_SAVE
        });
      },

      handleNameAvailabilityResponse(data) {
        if (data && typeof data.available === 'boolean') {
          applySnapshot(self, { ...getSnapshot(self), lastError: null });
          publish(constants.GET_PROMPT_NAME_AVAILABILITY_SUCCESS, { data });
          return;
        }
        const serverMessage = data?.error || data?.message;
        if (serverMessage) {
          fail(serverMessage, constants.GET_PROMPT_NAME_AVAILABILITY_ERROR, { message: serverMessage });
          return;
        }
        fail(PROMPT_MANAGEMENT_LANG_KEYS.ERROR_NAME_AVAILABILITY, constants.GET_PROMPT_NAME_AVAILABILITY_ERROR, {
          langMessage: PROMPT_MANAGEMENT_LANG_KEYS.ERROR_NAME_AVAILABILITY
        });
      },

      handleCreateResponse(data) {
        if (data && data.success !== false) {
          const mapped = mergeResolvedDocument(null, {
            ...data,
            id: data.id || data.promptRef,
            isAgentic: data.isAgentic === true || data.isAgentic === false ? data.isAgentic : true
          });
          const mutation = {
            promptRef: data.promptRef || data.id,
            promptVersionRef: data.promptVersionRef || data.version || '1.0.0',
            version: data.version || '1.0.0'
          };
          applySnapshot(self, {
            ...getSnapshot(self),
            lastError: null,
            lastMutation: mutation,
            activeResolved: mapped
          });
          publish(constants.CREATE_PROMPT_SUCCESS, { data: mapped || data });
          return;
        }
        const serverMessage = data?.error || data?.message;
        if (serverMessage) {
          fail(serverMessage, constants.CREATE_PROMPT_ERROR, { message: serverMessage });
          return;
        }
        fail(PROMPT_MANAGEMENT_LANG_KEYS.ERROR_CREATE, constants.CREATE_PROMPT_ERROR, {
          langMessage: PROMPT_MANAGEMENT_LANG_KEYS.ERROR_CREATE
        });
      }
    };
  });

export default StudioPromptModel;
